import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useCallback, useEffect, useState } from "react";
import { LlmInfo } from "../models/LlmInfo";
import { LlmInfoShort } from "../models/LlmInfoShort";
import { useGetLlmInfoDetails } from "../api/useGetLlmInfoDetails";
import { useCreateLlm } from "../api/useCreateLlm";
import { useUpdateLlm } from "../api/useUpdateLlm";
import CenterLoader from "@/shared/components/loader/CenterLoader";
import { X, Plus } from "lucide-react";

interface AddEditLlmModalProps {
  open: boolean;
  onClose: () => void;
  llmName?: string; // If provided, it's edit mode
  onSuccess?: () => void; // Called when LLM is successfully created/updated
  availableLlms: LlmInfoShort[]; // List of available LLMs for selection
}

export function AddEditLlmModal({
  open,
  onClose,
  llmName,
  onSuccess,
  availableLlms,
}: AddEditLlmModalProps) {
  const { llmInfoDetails, getLlmInfoDetails, isLoading } =
    useGetLlmInfoDetails();
  const { createLlm, isCreated, isFailed: isCreateFailed } = useCreateLlm();
  const { updateLlm, isUpdated, isFailed: isUpdateFailed } = useUpdateLlm();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<LlmInfo>>({
    name: "",
    displayName: "",
    description: "",
    speed: 1,
    intelligence: 1,
    contextWindowTokens: 0,
    maxOutputTokens: 0,
    defaultTemperature: 0.1,
    fallbackLlmNames: [],
    maxThinkingTokens: undefined,
    enableThinking: false,
    longContextLlmName: "",
  });

  const isEditMode = !!llmName;

  useEffect(() => {
    if (open && isEditMode && llmName) {
      getLlmInfoDetails(llmName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode, llmName]);

  useEffect(() => {
    if (llmInfoDetails) {
      setFormData({
        name: llmInfoDetails.name,
        displayName: llmInfoDetails.displayName,
        description: llmInfoDetails.description,
        speed: llmInfoDetails.speed,
        intelligence: llmInfoDetails.intelligence,
        contextWindowTokens: llmInfoDetails.contextWindowTokens,
        maxOutputTokens: llmInfoDetails.maxOutputTokens,
        defaultTemperature: llmInfoDetails.defaultTemperature,
        fallbackLlmNames: llmInfoDetails.fallbackLlmNames,
        maxThinkingTokens: llmInfoDetails.maxThinkingTokens,
        enableThinking: llmInfoDetails.enableThinking,
        longContextLlmName: llmInfoDetails.longContextLlmName,
      });
    }
  }, [llmInfoDetails]);

  const handleClose = useCallback(() => {
    setFormData({
      name: "",
      displayName: "",
      description: "",
      speed: 1,
      intelligence: 1,
      contextWindowTokens: 0,
      maxOutputTokens: 0,
      defaultTemperature: 0.1,
      fallbackLlmNames: [],
      maxThinkingTokens: undefined,
      enableThinking: false,
      longContextLlmName: "",
    });
    onClose();
  }, [onClose]);

  // Close modal on successful create
  useEffect(() => {
    if (isCreated && !isCreateFailed) {
      onSuccess?.(); // Trigger refresh in parent
      handleClose();
    }
  }, [isCreated, isCreateFailed, onSuccess, handleClose]);

  // Close modal on successful update
  useEffect(() => {
    if (isUpdated && !isUpdateFailed) {
      onSuccess?.(); // Trigger refresh in parent
      handleClose();
    }
  }, [isUpdated, isUpdateFailed, onSuccess, handleClose]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    if (isEditMode && llmName) {
      // Update existing LLM
      await updateLlm(llmName, formData);
    } else {
      // Create new LLM
      await createLlm(formData);
    }

    setIsSaving(false);
    // Modal will close automatically via useEffect when isCreated/isUpdated becomes true
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit LLM" : "Add LLM"}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <CenterLoader />
        ) : (
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Unique key</Label>
                <Input
                  id="name"
                  value={formData.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., gpt-4"
                  disabled={isEditMode}
                />
              </div>
              <div>
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={formData.displayName || ""}
                  onChange={(e) =>
                    handleInputChange("displayName", e.target.value)
                  }
                  placeholder="e.g., GPT-4"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ""}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Enter LLM description..."
                rows={3}
              />
            </div>

            {/* Performance Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="speed">Speed</Label>
                <Input
                  id="speed"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.speed || 1}
                  onChange={(e) =>
                    handleInputChange("speed", parseInt(e.target.value))
                  }
                />
              </div>
              <div>
                <Label htmlFor="intelligence">Intelligence</Label>
                <Input
                  id="intelligence"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.intelligence || 1}
                  onChange={(e) =>
                    handleInputChange("intelligence", parseInt(e.target.value))
                  }
                />
              </div>
            </div>

            {/* Token Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contextWindowTokens">
                  Context Window Tokens
                </Label>
                <Input
                  id="contextWindowTokens"
                  type="number"
                  min="0"
                  value={formData.contextWindowTokens || 0}
                  onChange={(e) =>
                    handleInputChange(
                      "contextWindowTokens",
                      parseInt(e.target.value),
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor="maxOutputTokens">Max Output Tokens</Label>
                <Input
                  id="maxOutputTokens"
                  type="number"
                  min="0"
                  value={formData.maxOutputTokens || 0}
                  onChange={(e) =>
                    handleInputChange(
                      "maxOutputTokens",
                      parseInt(e.target.value),
                    )
                  }
                />
              </div>
            </div>

            <div>
              <Label htmlFor="defaultTemperature">Default Temperature</Label>
              <Input
                id="defaultTemperature"
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={formData.defaultTemperature || 0.7}
                onChange={(e) =>
                  handleInputChange(
                    "defaultTemperature",
                    parseFloat(e.target.value),
                  )
                }
              />
            </div>

            {/* Fallback LLMs */}
            <div>
              <Label>Fallback LLM Names</Label>
              <div className="space-y-2">
                {formData.fallbackLlmNames?.map((fallbackName, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Select
                      value={fallbackName}
                      onValueChange={(value) => {
                        const newFallbackNames = [
                          ...(formData.fallbackLlmNames || []),
                        ];
                        newFallbackNames[index] = value;
                        handleInputChange("fallbackLlmNames", newFallbackNames);
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select a fallback LLM..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableLlms
                          .filter((llm) => llm.name !== formData.name) // Exclude current LLM
                          .map((llm) => (
                            <SelectItem key={llm.name} value={llm.name}>
                              {llm.displayName} ({llm.name})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        const newFallbackNames =
                          formData.fallbackLlmNames?.filter(
                            (_, i) => i !== index,
                          ) || [];
                        handleInputChange("fallbackLlmNames", newFallbackNames);
                      }}
                      className="shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newFallbackNames = [
                      ...(formData.fallbackLlmNames || []),
                      "",
                    ];
                    handleInputChange("fallbackLlmNames", newFallbackNames);
                  }}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Fallback LLM
                </Button>
              </div>
            </div>

            {/* Thinking Settings */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="enableThinking"
                  checked={formData.enableThinking || false}
                  onCheckedChange={(checked) =>
                    handleInputChange("enableThinking", checked)
                  }
                />
                <Label htmlFor="enableThinking">Enable Thinking</Label>
              </div>

              {formData.enableThinking && (
                <div>
                  <Label htmlFor="maxThinkingTokens">Max Thinking Tokens</Label>
                  <Input
                    id="maxThinkingTokens"
                    type="number"
                    min="0"
                    value={formData.maxThinkingTokens || 0}
                    onChange={(e) =>
                      handleInputChange(
                        "maxThinkingTokens",
                        parseInt(e.target.value),
                      )
                    }
                  />
                </div>
              )}
            </div>

            {/* Optional Settings */}
            <div>
              <Label htmlFor="longContextLlmName">Long Context LLM Name</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={formData.longContextLlmName || ""}
                  onValueChange={(value) =>
                    handleInputChange("longContextLlmName", value)
                  }
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a long context LLM..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLlms
                      .filter((llm) => llm.name !== formData.name) // Exclude current LLM
                      .map((llm) => (
                        <SelectItem key={llm.name} value={llm.name}>
                          {llm.displayName} ({llm.name})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {formData.longContextLlmName && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleInputChange("longContextLlmName", "")}
                    className="shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || isSaving}>
            {isSaving ? "Saving..." : isEditMode ? "Update LLM" : "Add LLM"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
